import React, { useMemo } from 'react';

const MAX_TABLE_ROWS = 25;
const MD_LINK_RE = /\[([^\]]+)\]\(([^)]+)\)/g;
const MD_IMAGE_RE = /!\[([^\]]*)\]\(([^)]+)\)/;
const FAKE_HOST_RE = /^https?:\/\/(?:your-dashboard-url|localhost(?::\d+)?|127\.0\.0\.1(?::\d+)?)(\/dashboard\/institute\S*)/i;
const IMAGE_URL_RE = /^https?:\/\/\S+\.(?:png|jpe?g|gif|webp|avif)(?:\?\S*)?$/i;
const PEXELS_OR_CDN_IMAGE_RE =
    /^https?:\/\/(?:images\.pexels\.com|cdn\.pixabay\.com|images\.unsplash\.com)\S+/i;

function isRenderableImageUrl(raw) {
    const href = String(raw || '').trim();
    if (!href) return false;
    if (href.startsWith('data:image/')) return true;
    if (/^https?:\/\/(?:localhost|127\.0\.0\.1)(?::\d+)?\/?$/i.test(href)) return false;
    if (href === '/' || href === '#') return false;
    return IMAGE_URL_RE.test(href) || PEXELS_OR_CDN_IMAGE_RE.test(href) || /^https?:\/\/.+\.(?:cdn|img)/i.test(href);
}

function normalizeLinkTarget(rawHref) {
    const href = String(rawHref || '').trim();
    if (!href) return null;

    const fake = FAKE_HOST_RE.exec(href);
    if (fake) {
        return { kind: 'dashboard', path: fake[1] };
    }

    if (href.startsWith('/dashboard/institute')) {
        return { kind: 'dashboard', path: href };
    }

    const dashIdx = href.indexOf('/dashboard/institute');
    if (dashIdx >= 0 && (href.startsWith('http') || href.includes('your-dashboard'))) {
        return { kind: 'dashboard', path: href.slice(dashIdx) };
    }

    if (href.startsWith('http://') || href.startsWith('https://')) {
        return { kind: 'external', href };
    }

    return { kind: 'text', href };
}

function MarkdownImage({ src, alt, onOpenStudio }) {
    const url = String(src || '').trim();
    if (!isRenderableImageUrl(url)) return null;

    if (typeof onOpenStudio === 'function') {
        return (
            <button
                type="button"
                className="md-image-link md-image-studio-trigger"
                onClick={() => onOpenStudio({ src: url, alt: alt || 'Product' })}
                title="Open image studio"
            >
                <img className="md-image" src={url} alt={alt || 'Product'} loading="lazy" />
                <span className="md-image-studio-badge">Studio</span>
            </button>
        );
    }

    return (
        <a className="md-image-link" href={url} target="_blank" rel="noreferrer">
            <img className="md-image" src={url} alt={alt || 'Product'} loading="lazy" />
        </a>
    );
}

function cellContent(text, onNavigate, onOpenStudio) {
    const raw = String(text || '').trim();
    const imgMatch = MD_IMAGE_RE.exec(raw);
    if (imgMatch && isRenderableImageUrl(imgMatch[2])) {
        return <MarkdownImage src={imgMatch[2].trim()} alt={imgMatch[1] || 'Product'} onOpenStudio={onOpenStudio} />;
    }
    if (isRenderableImageUrl(raw)) {
        return <MarkdownImage src={raw} alt="Product" onOpenStudio={onOpenStudio} />;
    }
    // Comma-separated image links (first shown)
    if (raw.includes(',') && /^https?:\/\//i.test(raw)) {
        const first = raw
            .split(',')
            .map((s) => s.trim())
            .find((s) => isRenderableImageUrl(s));
        if (first) return <MarkdownImage src={first} alt="Product" onOpenStudio={onOpenStudio} />;
    }
    return inlineFormat(text, onNavigate, onOpenStudio);
}

function inlineFormat(text, onNavigate, onOpenStudio) {
    if (!text) return null;

    const parts = [];
    const pattern = /(!\[[^\]]*\]\([^)]+\)|\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g;
    let lastIndex = 0;
    let match;
    let key = 0;

    while ((match = pattern.exec(text)) !== null) {
        if (match.index > lastIndex) {
            parts.push(text.slice(lastIndex, match.index));
        }

        const token = match[0];
        if (token.startsWith('![')) {
            const img = MD_IMAGE_RE.exec(token);
            const src = String(img?.[2] || '').trim();
            const alt = img?.[1] || 'Image';
            if (isRenderableImageUrl(src)) {
                parts.push(<MarkdownImage key={key++} src={src} alt={alt} onOpenStudio={onOpenStudio} />);
            }
            // drop broken !Image with empty/bad urls
        } else if (token.startsWith('[') && token.includes('](')) {
            MD_LINK_RE.lastIndex = 0;
            const linkMatch = MD_LINK_RE.exec(token);
            const label = linkMatch?.[1] || token;
            const target = normalizeLinkTarget(linkMatch?.[2] || '');
            if (target?.kind === 'dashboard' && typeof onNavigate === 'function') {
                parts.push(
                    <button
                        key={key++}
                        type="button"
                        className="md-nav-link"
                        onClick={() => onNavigate(target.path, label)}
                    >
                        {label}
                    </button>,
                );
            } else if (target?.kind === 'dashboard') {
                parts.push(
                    <span key={key++} className="md-nav-link static">
                        {label}
                    </span>,
                );
            } else if (target?.kind === 'external' && isRenderableImageUrl(target.href)) {
                parts.push(<MarkdownImage key={key++} src={target.href} alt={label} onOpenStudio={onOpenStudio} />);
            } else if (target?.kind === 'external') {
                parts.push(
                    <a key={key++} className="md-ext-link" href={target.href} target="_blank" rel="noreferrer">
                        {label}
                    </a>,
                );
            } else {
                parts.push(<strong key={key++}>{label}</strong>);
            }
        } else if (token.startsWith('**')) {
            parts.push(<strong key={key++}>{token.slice(2, -2)}</strong>);
        } else if (token.startsWith('*')) {
            parts.push(<em key={key++}>{token.slice(1, -1)}</em>);
        } else if (token.startsWith('`')) {
            parts.push(
                <code key={key++} className="md-code">
                    {token.slice(1, -1)}
                </code>,
            );
        }

        lastIndex = match.index + token.length;
    }

    if (lastIndex < text.length) {
        parts.push(text.slice(lastIndex));
    }

    return parts.length ? parts : text;
}

function parseTable(lines, onNavigate, onOpenStudio) {
    const rows = lines.map((line) =>
        line
            .trim()
            .replace(/^\|/, '')
            .replace(/\|$/, '')
            .split('|')
            .map((cell) => cell.trim()),
    );

    if (rows.length < 2) return null;

    const isSeparator = (row) => row.every((cell) => /^:?-{3,}:?$/.test(cell));
    const header = rows[0];
    if (!header.length || header.length > 20) return null;

    const bodyStart = isSeparator(rows[1]) ? 2 : 1;
    const body = rows.slice(bodyStart, bodyStart + MAX_TABLE_ROWS);

    return (
        <div className="md-table-wrap">
            <table className="md-table">
                <thead>
                    <tr>
                        {header.map((cell, i) => (
                            <th key={i}>{inlineFormat(cell, onNavigate, onOpenStudio)}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {body.map((row, ri) => (
                        <tr key={ri}>
                            {header.map((_, ci) => (
                                <td key={ci}>{cellContent(row[ci] || '', onNavigate, onOpenStudio)}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
            {rows.length - bodyStart > MAX_TABLE_ROWS ? (
                <div className="md-table-more">Showing first {MAX_TABLE_ROWS} rows…</div>
            ) : null}
        </div>
    );
}

function renderBlocks(markdown, onNavigate, onOpenStudio) {
    // Strip broken image markdown that resolves to "/" and triggers GET http://localhost:3000/
    const cleaned = String(markdown || '')
        .replace(/\r\n/g, '\n')
        .replace(/!\[[^\]]*\]\(\s*\)/g, '')
        .replace(/!\[[^\]]*\]\(\/\)/g, '')
        .replace(/!\[[^\]]*\]\(https?:\/\/(?:localhost|127\.0\.0\.1)(?::\d+)?\/?\)/gi, '');
    const lines = cleaned.split('\n');
    const blocks = [];
    let i = 0;
    let key = 0;
    let guard = 0;

    while (i < lines.length) {
        guard += 1;
        if (guard > lines.length + 50) break;

        const line = lines[i];

        if (!line.trim()) {
            i += 1;
            continue;
        }

        const soloImage = /^\s*!\[([^\]]*)\]\(([^)]+)\)\s*$/.exec(line);
        if (soloImage && isRenderableImageUrl(soloImage[2].trim())) {
            blocks.push(
                <div key={key++} className="md-image-block">
                    <MarkdownImage
                        src={soloImage[2].trim()}
                        alt={soloImage[1] || 'Image'}
                        onOpenStudio={onOpenStudio}
                    />
                </div>,
            );
            i += 1;
            continue;
        }

        if (
            line.trim().startsWith('|') &&
            i + 1 < lines.length &&
            /^\s*\|?\s*:?-{3,}/.test(lines[i + 1])
        ) {
            const tableLines = [];
            while (i < lines.length && lines[i].trim().startsWith('|')) {
                tableLines.push(lines[i]);
                i += 1;
                if (tableLines.length > MAX_TABLE_ROWS + 5) break;
            }
            const table = parseTable(tableLines, onNavigate, onOpenStudio);
            if (table) {
                blocks.push(React.cloneElement(table, { key: key++ }));
            } else {
                blocks.push(
                    <p key={key++} className="md-p">
                        {inlineFormat(tableLines.join(' '), onNavigate, onOpenStudio)}
                    </p>,
                );
            }
            continue;
        }

        if (line.trim().startsWith('|')) {
            const plain = [];
            while (i < lines.length && lines[i].trim().startsWith('|')) {
                plain.push(lines[i]);
                i += 1;
            }
            blocks.push(
                <pre key={key++} className="md-stream-plain">
                    {plain.join('\n')}
                </pre>,
            );
            continue;
        }

        const heading = /^(#{1,3})\s+(.*)$/.exec(line);
        if (heading) {
            const level = heading[1].length;
            const Tag = level === 1 ? 'h3' : level === 2 ? 'h4' : 'h5';
            blocks.push(
                <Tag key={key++} className={`md-h md-h${level}`}>
                    {inlineFormat(heading[2], onNavigate, onOpenStudio)}
                </Tag>,
            );
            i += 1;
            continue;
        }

        if (/^\s*[-*]\s+/.test(line)) {
            const items = [];
            while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
                items.push(lines[i].replace(/^\s*[-*]\s+/, ''));
                i += 1;
            }
            blocks.push(
                <ul key={key++} className="md-list">
                    {items.map((item, idx) => (
                        <li key={idx}>{inlineFormat(item, onNavigate, onOpenStudio)}</li>
                    ))}
                </ul>,
            );
            continue;
        }

        if (/^\s*\d+\.\s+/.test(line)) {
            const items = [];
            while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
                items.push(lines[i].replace(/^\s*\d+\.\s+/, ''));
                i += 1;
            }
            blocks.push(
                <ol key={key++} className="md-list md-ol">
                    {items.map((item, idx) => (
                        <li key={idx}>{inlineFormat(item, onNavigate, onOpenStudio)}</li>
                    ))}
                </ol>,
            );
            continue;
        }

        const para = [];
        while (
            i < lines.length &&
            lines[i].trim() &&
            !lines[i].trim().startsWith('|') &&
            !/^(#{1,3})\s+/.test(lines[i]) &&
            !/^\s*[-*]\s+/.test(lines[i]) &&
            !/^\s*\d+\.\s+/.test(lines[i])
        ) {
            para.push(lines[i]);
            i += 1;
        }
        blocks.push(
            <p key={key++} className="md-p">
                {inlineFormat(para.join(' '), onNavigate, onOpenStudio)}
            </p>,
        );
    }

    return blocks;
}

export default function MarkdownMessage({ content, streaming, onNavigate, onOpenStudio }) {
    const blocks = useMemo(() => {
        if (streaming) return null;
        return renderBlocks(content, onNavigate, onOpenStudio);
    }, [content, streaming, onNavigate, onOpenStudio]);

    if (streaming) {
        return (
            <div className="md-body">
                <pre className="md-stream-plain">{content || ''}</pre>
                <span className="streaming-cursor">▍</span>
            </div>
        );
    }

    return <div className="md-body">{blocks}</div>;
}
